import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAuth0 } from '@auth0/auth0-react';
import { useProfile } from '../contexts/ProfileContext'; // 🆕 JAN 26, 2026: Profile refresh on Settings page
import NotificationService from '../utils/notifications';
import { getApiBaseUrl } from '../utils/api';
import { storage } from '../services/storage';

import { CmpBootomnavCoach } from '../anima/Navigation Component/src/components/CmpBootomnavCoach/CmpBootomnavCoach';
import { ProfileDrawer } from '../anima/Settings/Setings - user Profile Drawer /src/screens/ProfileDrawer/ProfileDrawer';
import { SubscriptionDrawer } from './settings/SubscriptionDrawer';  // 💰 Sprint 2, Day 6
import { PrivacyDrawer } from './settings/PrivacyDrawer';
import { ServiceConnectorModal, ServiceType, PlatformType } from './ServiceConnectorModal';
import { FeedbackDrawer } from './settings/FeedbackDrawer';
import { DeleteDataDrawer } from './settings/DeleteDataDrawer';
import { AppearanceDrawer } from './settings/AppearanceDrawer';
import { MorningTouchBaseDrawer } from './settings/MorningTouchBaseDrawer';  // 🆕 MISSION 32
import { GarminDrawer } from './settings/GarminDrawer';
import { StravaDrawer } from './settings/StravaDrawer';
import stravaService from '../services/stravaService';
import { HRZonesDrawer } from './settings/HRZonesDrawer'; // 🆕 PHASE 4.2: HR Zones manual entry
import { HealthIconSquareWeb } from './settings/HealthIconSquareWeb'; // Web parity with native HealthIconSquare (iOS/Android)
import garminLogo from '../../assets/garmin.png'; // Data and Devices drawer – single source: web/assets/garmin.png

// (Anima settings icons/components removed from Settings grid redesign)

// Import Anima CSS files
import '../anima/Settings/Settings Home/src/screens/SettingsPage/style.css';
import '../anima/Settings/Setings - user Profile Drawer /src/screens/ProfileDrawer/style.css';
// Strava drawer CSS removed - Strava integration disabled from frontend (Feb 2026)
import '../anima/Settings/Settings - Delete Drawer /src/screens/DeteteDataDrawer/style.css';
import '../anima/Navigation Component/src/components/CmpBootomnavCoach/style.css';
import '../pages/Chat.css';
import './Settings.css';

type DrawerType =
  | 'profile'
  | 'subscription'  // 💰 Sprint 2, Day 6: Subscription management
  | 'privacy'
  | 'devices'
  | 'morning_touchbase'  // 🆕 MISSION 32: Morning check-in settings
  | 'notifications'
  | 'appearance'
  | 'garmin'
  | 'strava'  // 🔌 Strava connection drawer (re-added with tier-gated light switch)
  | 'applehealth'
  | 'googlefit'
  | 'feedback'
  | 'delete'
  | 'hr_zones'  // 🆕 PHASE 4.2: HR Zones drawer
  | null;

export const Settings: React.FC = () => {
  const { user, logout: coachLogout } = useAuth();
  const { logout: auth0Logout } = useAuth0();
  const { refreshProfile } = useProfile(); // 🆕 JAN 26, 2026: Refresh profile data
  const navigate = useNavigate();
  const [openDrawer, setOpenDrawer] = useState<DrawerType>(null);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  // Strava state — tier-gated light switch (re-added alongside Garmin).
  // Default OFF (2026-06-01): Strava rejected our Developer Program app, so
  // the integration is globally disabled. The tile renders "Coming Soon"
  // (disabled) until /api/auth/strava/status reports strava_available=true
  // (only if STRAVA_INTEGRATION_ENABLED is re-enabled server-side). Defaulting
  // false avoids an available→coming-soon flash on first render.
  const [stravaConnected, setStravaConnected] = useState(false);
  const [stravaAvailable, setStravaAvailable] = useState(false);
  const [garminConnected, setGarminConnected] = useState(false);
  const [garminAvailable, setGarminAvailable] = useState(true);
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [currentService, setCurrentService] = useState<ServiceType | null>(null);
  const [userPlatform, setUserPlatform] = useState<PlatformType>('desktop');

  // Prevent background scroll while a bottom-sheet is open
  useEffect(() => {
    if (!openDrawer) return;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.documentElement.style.overflow = prevHtmlOverflow;
    };
  }, [openDrawer]);

  // Detect platform on mount
  useEffect(() => {
    const detectPlatform = (): PlatformType => {
      const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
      
      if (/android/i.test(userAgent)) {
        return 'android';
      }
      
      if (/iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream) {
        return 'ios';
      }
      
      return 'desktop';
    };
    
    setUserPlatform(detectPlatform());
  }, []);

  const handleNavigation = (tab: string) => {
    switch (tab) {
      case 'cedar':
        navigate('/chat');
        break;
      case 'my-path':
      case 'mypath':
        navigate('/mypath');
        break;
      // HEALTH SECTION TEMPORARILY REMOVED FOR MVP
      // case 'health':
      //   navigate('/health');
      //   break;
      case 'momentum':
      case 'insights':
        navigate('/insights');
        break;
      case 'settings':
        // Already on settings page
        break;
      default:
        break;
    }
  };

  // Fetch user settings on mount + refresh profile
  useEffect(() => {
    checkStravaConnection();
    checkGarminConnection();
    refreshProfile(); // 🆕 JAN 26, 2026: Refresh profile when Settings page loads
  }, [user?.id]); // 🚨 Don't add refreshProfile to deps - causes infinite loop!

  const checkStravaConnection = async () => {
    if (!user?.id) return;
    try {
      const data = await stravaService.getConnectionStatus();
      setStravaConnected(data.connected);
      if (data.strava_available !== undefined) {
        setStravaAvailable(data.strava_available);
      }
    } catch (error) {
      console.warn('Failed to check Strava connection:', error);
    }
  };

  const checkGarminConnection = async () => {
    if (!user?.id) return;

    try {
      const jwtToken = await storage.getItem('jwt_token') || await storage.getItem('token');
      if (!jwtToken) return;

      const response = await fetch('/api/auth/garmin/status', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${jwtToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setGarminConnected(data.connected);
        if (data.garmin_available !== undefined) {
          setGarminAvailable(data.garmin_available);
        }
      }
    } catch (error) {
      console.error('Failed to check Garmin connection:', error);
    }
  };

  const handleNotificationToggle = async () => {
    if (!user?.id || notificationLoading) return;
    const newState = !notificationsEnabled;
    setNotificationsEnabled(newState); // Optimistically update
    setNotificationLoading(true);
    try {
      if (newState) {
        // If enabling notifications, first check browser permission
        const notificationService = NotificationService.getInstance();
        const permissionStatus = notificationService.getPermissionStatus();
        if (permissionStatus === 'denied') {
          alert('Please enable notifications in your browser settings to receive notifications from Coach Cedar.');
          setNotificationsEnabled(false);
          setNotificationLoading(false);
          return;
        }
        if (permissionStatus === 'default') {
          const granted = await notificationService.requestPermissionAndSubscribe();
          if (!granted) {
            setNotificationsEnabled(false);
            setNotificationLoading(false);
            return;
          }
        }
      }
      // Access-Control Airtight (2026-09): the backend write that used to sit here
      // (a PUT to the per-user notification-settings placeholder route) hit an
      // unauthenticated in-memory stub that persisted nothing and drove nothing;
      // the sprint deleted the route. The browser subscription above is the real
      // state, so the toggle no longer round-trips: same behaviour for the user,
      // without an alert on a route that is gone.
    } catch {
      setNotificationsEnabled(!newState); // Revert
      alert('Failed to update notification setting. Please try again.');
    } finally {
      setNotificationLoading(false);
    }
  };

  const handleOpenDrawer = (drawer: DrawerType) => {
    console.log('🔧 Opening drawer:', drawer);
    
    // Map drawer types to service types
    // Note: Garmin and Strava each have their own dedicated drawer, not ServiceConnectorModal
    if (drawer === 'garmin') {
      // Garmin has dedicated drawer - don't set service for modal
      setOpenDrawer('garmin');
      checkGarminConnection(); // Refresh connection status
      return;
    } else if (drawer === 'strava') {
      // Strava has dedicated drawer - don't set service for modal
      setOpenDrawer('strava');
      checkStravaConnection(); // Refresh connection status
      return;
    } else if (drawer === 'applehealth') {
      setCurrentService('apple_health');
    } else if (drawer === 'googlefit') {
      setCurrentService('google_fit');
    }
    
    setOpenDrawer(drawer);
  };

  const handleCloseDrawer = () => {
    setOpenDrawer(null);
    setCurrentService(null);
  };

  // M35B: Swipe-to-close for web drawer (touch devices)
  const drawerTouchStartY = useRef<number | null>(null);
  const handleDrawerTouchStart = (e: React.TouchEvent) => {
    drawerTouchStartY.current = e.touches[0].clientY;
  };
  const handleDrawerTouchEnd = (e: React.TouchEvent) => {
    if (drawerTouchStartY.current !== null) {
      const dy = e.changedTouches[0].clientY - drawerTouchStartY.current;
      if (dy > 80) {
        handleCloseDrawer();
      }
      drawerTouchStartY.current = null;
    }
  };
  // M35B Sprint 1: Reset on interrupted touch to prevent stale state
  const handleDrawerTouchCancel = () => {
    drawerTouchStartY.current = null;
  };

  const handleServiceConnect = async (service: ServiceType) => {
    console.log('Connecting service:', service);
    
    try {
      const jwtToken = await storage.getItem('jwt_token') || await storage.getItem('token');
      if (!jwtToken) {
        throw new Error('No authentication token found');
      }
      
      // Strava OAuth flow removed - Strava integration disabled from frontend (Feb 2026)
      if (service === 'garmin') {
        const apiBaseUrl = getApiBaseUrl();
        const response = await fetch(`${apiBaseUrl}/api/auth/garmin/connect`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${jwtToken}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Failed to get Garmin authorization URL');
        }

        const { authorization_url } = await response.json();
        
        // Store return URL for after OAuth
        await storage.setItem('garmin_return_url', '/settings');
        
        // Redirect to Garmin OAuth
        if (userPlatform === 'ios' || userPlatform === 'android') {
          window.location.href = authorization_url;
        } else {
          const newWindow = window.open(authorization_url, '_blank');
          if (!newWindow) {
            window.location.href = authorization_url;
          }
        }
        
        // Mark as connected (will be verified on return)
        setGarminConnected(true);
        handleCloseDrawer();
      }
    } catch (error) {
      console.error('Service connection error:', error);
      throw error;
    }
  };

  // Handle navigation clicks from the bottom nav
  useEffect(() => {
    const handleNavClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const navItem = target.closest('[data-tab]') as HTMLElement;
      
      if (navItem) {
        const tab = navItem.dataset.tab;
        if (tab) {
          handleNavigation(tab);
        }
      }
    };

    document.addEventListener('click', handleNavClick);

    return () => {
      document.removeEventListener('click', handleNavClick);
    };
  }, [handleNavigation]);

  const handleLogout = () => {
    // Confirm logout action
    if (window.confirm('Are you sure you want to sign out?')) {
      // 1) Clear Coach Cedar session (local user + jwt)
      coachLogout();

      // 2) Log out of Auth0 as well, otherwise we can get stuck in a half-authenticated state
      //    (Auth0 session active but Coach Cedar session cleared).
      try {
        auth0Logout({
          logoutParams: {
            returnTo: `${window.location.origin}/`, // land on root so Splash ("welcome back") shows
          },
        });
      } catch (e) {
        // Fallback: ensure we still leave the authed area even if Auth0 logout fails
        console.warn('⚠️ Auth0 logout failed, falling back to hard redirect', e);
        window.location.href = '/';
      }
    }
  };

  // Main settings component structure
  const settingsContent = (
    <div className="clean-chat-container settings-container flex flex-col h-full bg-white max-w-md mx-auto">
      <div className="flex-1 overflow-y-auto">
        <div className="settings-page">
          <div className="settings-content">
            <div className="settings-grid">
              <button type="button" className="settings-tile" onClick={() => handleOpenDrawer('profile')}>
                <div className="settings-tile-icon" aria-hidden="true">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 21a8 8 0 0 0-16 0" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                    <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.6" />
                  </svg>
                </div>
                <div className="settings-tile-label">Profile</div>
              </button>

              {/* 💰 Sprint 2, Day 6: Subscription Management */}
              <button type="button" className="settings-tile" onClick={() => handleOpenDrawer('subscription')}>
                <div className="settings-tile-icon" aria-hidden="true">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2L2 7l10 5 10-5-10-5z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
                    <path d="M2 17l10 5 10-5" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
                    <path d="M2 12l10 5 10-5" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
                  </svg>
                </div>
                <div className="settings-tile-label">Subscription</div>
              </button>

              {/* 🆕 MISSION 32: Morning Touch-Base Settings */}
              <button type="button" className="settings-tile" onClick={() => handleOpenDrawer('morning_touchbase')}>
                <div className="settings-tile-icon" aria-hidden="true">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.6" />
                    <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                    <path d="M6 6a9 9 0 0 1 12 0" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                  </svg>
                </div>
                <div className="settings-tile-label">Morning Touch-Base</div>
              </button>

              <button type="button" className="settings-tile" onClick={() => handleOpenDrawer('devices')}>
                <div className="settings-tile-icon" aria-hidden="true">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="6" y="4" width="12" height="16" rx="3" stroke="currentColor" strokeWidth="1.6" />
                    <path d="M9 4v2M15 4v2M9 20v-2M15 20v-2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                    <circle cx="12" cy="12" r="3.5" stroke="currentColor" strokeWidth="1.6" />
                    <path d="M12 9v3l2 1.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <div className="settings-tile-label">Connect Device</div>
              </button>

              {/* 🆕 PHASE 4.2: HR Zones tile */}
              <button type="button" className="settings-tile" onClick={() => handleOpenDrawer('hr_zones')}>
                <div className="settings-tile-icon" aria-hidden="true">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" stroke="currentColor" strokeWidth="1.6" />
                  </svg>
                </div>
                <div className="settings-tile-label">Heart Rate Zones</div>
              </button>

              <button
                type="button"
                className="settings-tile"
                onClick={() => handleOpenDrawer('notifications')}
              >
                <div className="settings-tile-icon" aria-hidden="true">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 17h12l-1-2v-4a5 5 0 0 0-10 0v4l-1 2z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
                    <path d="M10 17a2 2 0 0 0 4 0" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                  </svg>
                </div>
                <div className="settings-tile-label">Notifications</div>
              </button>

              <button type="button" className="settings-tile" onClick={() => handleOpenDrawer('appearance')}>
                <div className="settings-tile-icon" aria-hidden="true">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.6" />
                    <path d="M12 2v2M12 20v2M2 12h2M20 12h2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                    <path d="M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M19.1 4.9l-1.4 1.4M6.3 17.7l-1.4 1.4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                  </svg>
                </div>
                <div className="settings-tile-label">Appearance</div>
              </button>

              <button type="button" className="settings-tile" onClick={() => handleOpenDrawer('feedback')}>
                <div className="settings-tile-icon" aria-hidden="true">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M21 15a3 3 0 0 1-3 3H8l-5 4V6a3 3 0 0 1 3-3h12a3 3 0 0 1 3 3v9z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
                  </svg>
                </div>
                <div className="settings-tile-label">Feedback</div>
              </button>

              <button type="button" className="settings-tile" onClick={() => handleOpenDrawer('privacy')}>
                <div className="settings-tile-icon" aria-hidden="true">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2l8 4v6c0 5-3.5 9.8-8 10.9C7.5 21.8 4 17 4 12V6l8-4z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
                    <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <div className="settings-tile-label">Privacy</div>
              </button>
            </div>

            <div className="settings-footer">
              <button type="button" className="settings-signout" onClick={handleLogout}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  <polyline points="16,17 21,12 16,7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  <line x1="21" y1="12" x2="9" y2="12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                </svg>
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Navigation at bottom level to match other pages */}
      <div className="navigation-container">
        <CmpBootomnavCoach activeTab="settings" className="" onClickTab={handleNavigation as () => void} />
      </div>
    </div>
  );

  // If a drawer is open, render settings content with overlay
  if (openDrawer) {
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 20000 }}>
        {settingsContent}
        <div 
          className="drawer-overlay" 
          onClick={handleCloseDrawer}
        >
          <div 
            className="drawer-content" 
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="drawer-handle-zone"
              onClick={handleCloseDrawer}
              onTouchStart={handleDrawerTouchStart}
              onTouchEnd={handleDrawerTouchEnd}
              onTouchCancel={handleDrawerTouchCancel}
              role="button"
              tabIndex={0}
              aria-label="Close drawer"
            >
              <div className="drawer-handle" />
            </div>
            <button type="button" className="drawer-close" onClick={handleCloseDrawer} aria-label="Close">
              ×
            </button>
            <div className="drawer-scroll">
              {openDrawer === 'profile' && <ProfileDrawer />}
              {openDrawer === 'subscription' && <SubscriptionDrawer onClose={handleCloseDrawer} />}
              {openDrawer === 'privacy' && (
                <PrivacyDrawer 
                  onOpenDeleteDrawer={() => {
                    handleCloseDrawer();
                    setTimeout(() => setOpenDrawer('delete'), 100);
                  }} 
                />
              )}
              {openDrawer === 'devices' && (
                <div className="devices-drawer">
                  <div className="devices-drawer-title">Connect a device</div>
                  <div className="devices-drawer-grid">
                    <button type="button" className="devices-drawer-tile" onClick={() => handleOpenDrawer('garmin')} disabled={!garminAvailable} style={{ position: 'relative', opacity: garminAvailable ? 1 : 0.45, cursor: garminAvailable ? 'pointer' : 'default' }}>
                      {!garminAvailable && (
                        <span style={{
                          position: 'absolute',
                          top: '6px',
                          right: '6px',
                          fontSize: '10px',
                          fontWeight: '600',
                          color: '#888',
                          backgroundColor: '#f0f0f0',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          zIndex: 1
                        }}>Coming Soon</span>
                      )}
                      <div className="devices-drawer-icon devices-drawer-icon--garmin" aria-hidden="true">
                        <img
                          src={garminLogo}
                          alt="Garmin"
                          className="devices-drawer-garmin-logo"
                          onError={(e) => {
                            // Fallback to SVG clock icon if image not found (safe DOM manipulation)
                            e.currentTarget.style.display = 'none';
                            const parent = e.currentTarget.parentElement;
                            if (parent) {
                              const ns = 'http://www.w3.org/2000/svg';
                              const svg = document.createElementNS(ns, 'svg');
                              svg.setAttribute('width', '24');
                              svg.setAttribute('height', '24');
                              svg.setAttribute('viewBox', '0 0 24 24');
                              svg.setAttribute('fill', 'none');
                              const circle = document.createElementNS(ns, 'circle');
                              circle.setAttribute('cx', '12');
                              circle.setAttribute('cy', '12');
                              circle.setAttribute('r', '9');
                              circle.setAttribute('stroke', 'currentColor');
                              circle.setAttribute('stroke-width', '1.6');
                              const path = document.createElementNS(ns, 'path');
                              path.setAttribute('d', 'M12 6v6l4 2');
                              path.setAttribute('stroke', 'currentColor');
                              path.setAttribute('stroke-width', '1.6');
                              path.setAttribute('stroke-linecap', 'round');
                              path.setAttribute('stroke-linejoin', 'round');
                              svg.appendChild(circle);
                              svg.appendChild(path);
                              parent.appendChild(svg);
                            }
                          }}
                        />
                      </div>
                      <div className="devices-drawer-label">Garmin</div>
                      {garminConnected && garminAvailable && (
                        <span style={{
                          position: 'absolute',
                          top: '6px',
                          right: '6px',
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: '#1F6C4F',
                          border: '2px solid white',
                          boxShadow: '0 0 0 1px rgba(0,0,0,0.1)'
                        }} />
                      )}
                    </button>

                    {/* Strava tile — COMPLETELY HIDDEN unless the integration is
                        available. 2026-06-01: Strava rejected our Developer Program
                        application; the integration is off indefinitely, so the tile
                        is not rendered for anyone (no "Coming Soon" — that would
                        imply it's returning). It reappears only if Strava re-approves
                        and STRAVA_INTEGRATION_ENABLED is set server-side
                        (→ /api/auth/strava/status → strava_available=true). */}
                    {stravaAvailable && (
                      <button type="button" className="devices-drawer-tile" onClick={() => handleOpenDrawer('strava')} style={{ position: 'relative' }}>
                        <div className="devices-drawer-icon" aria-hidden="true" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <img
                            src="/strava-assets/api_logo_pwrdBy_strava_horiz_orange.png"
                            alt="Strava"
                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                            onError={(e) => {
                              // Fallback to a simple "S" badge if image missing
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        </div>
                        <div className="devices-drawer-label">Strava</div>
                        {stravaConnected && (
                          <span style={{
                            position: 'absolute',
                            top: '6px',
                            right: '6px',
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: '#1F6C4F',
                            border: '2px solid white',
                            boxShadow: '0 0 0 1px rgba(0,0,0,0.1)'
                          }} />
                        )}
                      </button>
                    )}

                    <button type="button" className="devices-drawer-tile devices-drawer-tile-health" onClick={() => handleOpenDrawer('applehealth')}>
                      <div className="devices-drawer-icon devices-drawer-icon-health" aria-hidden="true" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <HealthIconSquareWeb size={56} heartSize={24} transparentBackground variant="light" />
                      </div>
                      <div className="devices-drawer-label">Apple Health</div>
                    </button>

                    <button type="button" className="devices-drawer-tile devices-drawer-tile-health" onClick={() => handleOpenDrawer('googlefit')}>
                      <div className="devices-drawer-icon devices-drawer-icon-health" aria-hidden="true">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M5 14c0-5 3-9 7-9s7 4 7 9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                          <path d="M7 14h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                          <path d="M8 19h8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                        </svg>
                      </div>
                      <div className="devices-drawer-label">Google Fit</div>
                    </button>
                  </div>
                </div>
              )}
              {openDrawer === 'notifications' && (
                <div className="settings-sheet-panel">
                  <div className="settings-sheet-panel-title">Notifications</div>
                  <div className="settings-sheet-panel-subtitle">
                    Turn browser notifications on/off for Coach Cedar.
                  </div>

                  <button
                    type="button"
                    className={`settings-sheet-toggle ${notificationLoading ? 'disabled' : ''}`}
                    onClick={handleNotificationToggle}
                    disabled={notificationLoading}
                  >
                    <div className="settings-sheet-toggle-label">
                      {notificationsEnabled ? 'Enabled' : 'Disabled'}
                    </div>
                    <div className={`settings-sheet-toggle-pill ${notificationsEnabled ? 'on' : 'off'}`} />
                  </button>
                </div>
              )}

              {openDrawer === 'garmin' && <GarminDrawer onClose={handleCloseDrawer} />}
              {openDrawer === 'strava' && <StravaDrawer onClose={handleCloseDrawer} />}
              {openDrawer === 'appearance' && <AppearanceDrawer />}
              {openDrawer === 'hr_zones' && <HRZonesDrawer onClose={handleCloseDrawer} />}  {/* 🆕 PHASE 4.2: HR Zones manual entry */}
              {openDrawer === 'morning_touchbase' && <MorningTouchBaseDrawer isOpen={true} onClose={handleCloseDrawer} userId={user?.id || ''} />}  {/* 🆕 MISSION 32: Morning check-in settings */}
              {openDrawer === 'feedback' && <FeedbackDrawer onClose={handleCloseDrawer} />}
              {openDrawer === 'delete' && <DeleteDataDrawer onClose={handleCloseDrawer} />}
            </div>
          </div>
        </div>
        
        {/* Service Connection Modals */}
        {/* Note: Garmin uses GarminDrawer component, not ServiceConnectorModal */}
        {currentService && (openDrawer === 'applehealth' || openDrawer === 'googlefit') && (
          <ServiceConnectorModal
            service={currentService}
            platform={userPlatform}
            isOpen={true}
            onClose={handleCloseDrawer}
            onConnect={handleServiceConnect}
          />
        )}
      </div>
    );
  }

  return settingsContent;
}; 